import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

interface LazyImageProps {
  src: string;
  alt: string;
  fill?: boolean;
  className: string;
  width?: number;
  height?: number;
  loading: 'eager' | 'lazy';
  sizes?: string;
}

const LazyImage = ({
  src,
  alt,
  fill = false,
  className,
  width,
  height,
  loading = 'lazy',
  sizes,
}: LazyImageProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Create an IntersectionObserver to check if the image is in the viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // If the image is intersecting (in the viewport)
          if (entry.isIntersecting) {
            // Set isVisible state to true to trigger image loading
            setIsVisible(true);
            // Stop observing the image after it becomes visible
            observer.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.1 },
    );

    // Check if the imageRef is available. Start observing the image element
    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    // Cleanup function for when the component unmounts or observer changes
    return () => {
      // Stop observing the image to avoid memory leaks
      if (imageRef.current) {
        observer.unobserve(imageRef.current);
      }
    };
  }, []);

  return (
    <div ref={imageRef} className="h-full w-full">
      {isVisible ? (
        <Image
          src={src}
          alt={alt}
          fill={fill}
          className={className}
          width={width}
          height={height}
          loading={loading}
          sizes={sizes}
        />
      ) : null}
    </div>
  );
};

export default LazyImage;
